import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, Check, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stripe LIVE publishable key (safe to expose - this is a public key)
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51O0UisKf6Ko0Ec6r9LpRr9I2s93AlgsulglmQwoAZK73LahYG8fBLKr1v7Hoc1TWbx5yVtNRz6Z895a9t2XrzroV009aawzUr1';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      toast.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/subscription/create-checkout-session`, {
        success_url: `${window.location.origin}/subscription?success=true`,
        cancel_url: `${window.location.origin}/subscription?canceled=true`
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: response.data.session_id
        });
        if (error) {
          toast.error(error.message);
        }
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await axios.post(`${API}/subscription/create-portal-session`);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
      toast.error(error.response?.data?.detail || 'Failed to open subscription management');
    }
  };

  // Check URL params for success/cancel messages
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast.success('Subscription activated successfully!');
      fetchSubscriptionStatus();
      // Clean URL
      window.history.replaceState({}, '', '/subscription');
    } else if (urlParams.get('canceled') === 'true') {
      toast.info('Checkout was canceled');
      window.history.replaceState({}, '', '/subscription');
    }
  }, []);

  const getStatusBadge = () => {
    if (!subscriptionStatus) return null;

    const statusConfig = {
      trialing: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Trial' },
      active: { color: 'bg-green-100 text-green-800', icon: Check, label: 'Active' },
      past_due: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Past Due' },
      canceled: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Canceled' },
      none: { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: 'No Subscription' }
    };

    const config = statusConfig[subscriptionStatus.status] || statusConfig.none;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isTrialing = subscriptionStatus?.status === 'trialing';
  const isActive = subscriptionStatus?.status === 'active';
  const needsSubscription = !subscriptionStatus?.is_active;

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="subscription-page">
      <div>
        <h1 className="text-4xl font-bold text-secondary tracking-tight">Subscription</h1>
        <p className="text-muted-foreground mt-2">Manage your AquaPro Logs subscription</p>
      </div>

      {/* Current Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Subscription Status
            </CardTitle>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isTrialing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-blue-900">Free Trial Active</h3>
                  <p className="text-blue-700 text-sm mt-1">
                    You have <strong>{subscriptionStatus.days_remaining} days</strong> remaining in your free trial.
                    Subscribe now to continue using AquaPro Logs after your trial ends.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isActive && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-green-900">Subscription Active</h3>
                  <p className="text-green-700 text-sm mt-1">
                    Your subscription is active. Thank you for being an AquaPro Logs customer!
                  </p>
                </div>
              </div>
            </div>
          )}

          {needsSubscription && !isTrialing && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-900">Subscription Required</h3>
                  <p className="text-red-700 text-sm mt-1">
                    Your trial has ended. Please subscribe to continue using AquaPro Logs.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            <p>Account: {user?.email}</p>
            {subscriptionStatus?.trial_ends_at && (
              <p>Trial ends: {new Date(subscriptionStatus.trial_ends_at).toLocaleDateString()}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pricing Card */}
      <Card className="border-primary">
        <CardHeader>
          <CardTitle>AquaPro Logs Annual Plan</CardTitle>
          <CardDescription>Everything you need for pool compliance management</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-1 mb-6">
            <span className="text-4xl font-bold">$25</span>
            <span className="text-muted-foreground">/year</span>
          </div>
          
          <ul className="space-y-3">
            {[
              'Manage up to 4 pools',
              'Unlimited daily chemical readings',
              'Real-time compliance warnings',
              'QR codes for public pool status',
              'Export reports to spreadsheets',
              'VGB compliance document storage',
              '90-day reading history',
              'English & Spanish support'
            ].map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600" />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          {!isActive && (
            <Button 
              className="w-full" 
              size="lg"
              onClick={handleSubscribe}
              disabled={processingPayment}
              data-testid="subscribe-button"
            >
              {processingPayment ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {isTrialing ? 'Subscribe Now' : 'Start Subscription'}
                </>
              )}
            </Button>
          )}
          
          {isActive && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleManageSubscription}
              data-testid="manage-subscription-button"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Manage Subscription
            </Button>
          )}
          
          <p className="text-xs text-center text-muted-foreground">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubscriptionPage;
